import { useCallback } from 'react';

import {
	useCommentsData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { fg } from '@confluence/feature-gating';
import {
	useUnreadCommentsActions,
	useUnreadComments,
} from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { expVal } from '@confluence/feature-experiments';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';

import {
	createReadCommentsDataMap,
	getCommentIdsByTypeAndAnnotationId,
} from '../helper/commentHelpers';

export const useSyncReadComments = (pageId?: string) => {
	const { setCommentsToBeMarkedAsRead } = useCommentsPanelActions();
	const [{ commentsDataMap: legacyCommentsDataMap }, { setCommentsDataMap }] = useCommentsData();
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const [{ unreadCommentIDSet }, { setShouldClearVisibleThreads }] = useUnreadComments();
	const { updateViewedCommentsCache } = useViewedComments({ pageId: pageId || '' });
	const { contentStatus } = useContentStatus();

	// New comments data manager hook - only used when experiment is enabled
	const newCommentsDataResult = useAllCommentsData({
		contentId: pageId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(contentStatus),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !pageId,
		},
	});
	// Use the appropriate data source based on experiment
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	return useCallback(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			// Update the unread comments store
			// Get all current unread comment IDs to add to viewed comments cache
			const unreadCommentIds = Array.from(unreadCommentIDSet);

			// Add all unread comments to viewed comments cache
			if (pageId && unreadCommentIds.length > 0) {
				updateViewedCommentsCache(unreadCommentIds);
			}

			// Clear all unread comments
			updateUnreadCommentIDSet(new Set());
			setShouldClearVisibleThreads(true);
		} else {
			setCommentsToBeMarkedAsRead(getCommentIdsByTypeAndAnnotationId(commentsDataMap));
			setCommentsDataMap(createReadCommentsDataMap(commentsDataMap));
		}
	}, [
		commentsDataMap,
		setCommentsToBeMarkedAsRead,
		setCommentsDataMap,
		updateUnreadCommentIDSet,
		unreadCommentIDSet,
		pageId,
		updateViewedCommentsCache,
		setShouldClearVisibleThreads,
	]);
};
